import React, { useState, useEffect } from 'react';
import { X, MapPin, Mail, Phone, Globe, User, ExternalLink } from 'lucide-react';
import { demandsAPI } from '../../api';
import type { Demand } from '../../types';

interface ContactDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: any;
  onViewDemands: () => void;
}

const TYPE_LABEL: Record<string, { label: string; color: string }> = {
  PREFEITURA:  { label: 'Prefeitura',   color: 'text-blue-600   bg-blue-50   dark:bg-blue-950/30   dark:text-blue-300'   },
  CAMARA:      { label: 'Câmara',       color: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-950/30 dark:text-indigo-300' },
  HOSPITAL:    { label: 'Hospital',     color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-300' },
  ONG:         { label: 'ONG',          color: 'text-violet-600 bg-violet-50 dark:bg-violet-950/30 dark:text-violet-300' },
  EMPRESA:     { label: 'Empresa',      color: 'text-amber-600  bg-amber-50  dark:bg-amber-950/30  dark:text-amber-300'  },
  ASSOCIACAO:  { label: 'Associação',   color: 'text-rose-600   bg-rose-50   dark:bg-rose-950/30   dark:text-rose-300'   },
  PESSOA:      { label: 'Pessoa',       color: 'text-slate-600  bg-slate-100 dark:bg-slate-800     dark:text-slate-400'  },
  OUTRO:       { label: 'Outro',        color: 'text-slate-500  bg-slate-100 dark:bg-slate-800     dark:text-slate-400'  },
};

export const ContactDetailModal: React.FC<ContactDetailModalProps> = ({
  isOpen,
  onClose,
  contact,
  onViewDemands
}) => {
  const [demands, setDemands] = useState<Demand[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && contact?.nameEntity) {
      const fetchDemands = async () => {
        setLoading(true);
        try {
          const res = await demandsAPI.list();
          // Filtrar as demandas que pertencem a este contato pelo nameEntity
          const filtered = (res.data || []).filter((d: Demand) => 
            d.solicitanteName?.toLowerCase() === contact.nameEntity.toLowerCase()
          );
          setDemands(filtered);
        } catch (error) {
          console.error('Erro ao buscar demandas do contato:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchDemands();
    }
  }, [isOpen, contact]);

  if (!isOpen || !contact) return null;

  const type = TYPE_LABEL[contact.type] ?? { label: contact.type ?? 'Outro', color: 'text-slate-500 bg-slate-100' };

  // Formata moeda
  const formatCurrency = (val?: number) => {
    if (!val) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  // Status Badge Colors
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RECEBIDA': return 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300';
      case 'EM_TRIAGEM': return 'bg-amber-100 text-amber-800 dark:bg-amber-955 dark:text-amber-300';
      case 'EM_ANALISE': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300';
      case 'APROVADA': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300';
      case 'CONCLUIDA': return 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300';
      default: return 'bg-slate-150 text-slate-600 dark:bg-slate-900 dark:text-slate-400';
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-2xl max-h-[85vh] p-6 shadow-2xl relative text-left flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-6 top-6 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-slate-400 hover:text-slate-650 transition-all z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="mb-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${type.color}`}>
              {type.label}
            </span>
            <span className="text-[10px] text-slate-400">ID: {contact.id.substring(0, 8)}</span>
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{contact.nameEntity}</h2>
        </div>

        {/* Modal Content - Scrollable area */}
        <div className="flex-1 overflow-y-auto space-y-6 pr-1">
          {/* Informações Básicas Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Informações Básicas</h3>
              
              {contact.responsible && (
                <div className="flex items-center gap-2 text-xs">
                  <User className="w-4 h-4 text-slate-400 shrink-0" />
                  <div>
                    <p className="text-slate-400 text-[10px]">Responsável</p>
                    <p className="font-semibold text-slate-800 dark:text-slate-200">{contact.responsible}</p>
                  </div>
                </div>
              )}

              {contact.phone && (
                <div className="flex items-center gap-2 text-xs">
                  <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                  <div>
                    <p className="text-slate-400 text-[10px]">Telefone</p>
                    <a href={`tel:${contact.phone}`} className="font-semibold text-blue-500 hover:underline dark:text-blue-400 font-mono">
                      {contact.phone}
                    </a>
                  </div>
                </div>
              )}

              {contact.email && (
                <div className="flex items-center gap-2 text-xs">
                  <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                  <div>
                    <p className="text-slate-400 text-[10px]">E-mail</p>
                    <a href={`mailto:${contact.email}`} className="font-semibold text-blue-500 hover:underline dark:text-blue-400">
                      {contact.email}
                    </a>
                  </div>
                </div>
              )}

              {contact.website && (
                <div className="flex items-center gap-2 text-xs">
                  <Globe className="w-4 h-4 text-slate-400 shrink-0" />
                  <div>
                    <p className="text-slate-400 text-[10px]">Website</p>
                    <a href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-500 hover:underline dark:text-blue-400 flex items-center gap-1">
                      {contact.website} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Localização</h3>
              
              <div className="flex items-start gap-2 text-xs">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-slate-400 text-[10px]">Cidade / UF</p>
                  <p className="font-semibold text-slate-800 dark:text-slate-200">{contact.city} — {contact.uf}</p>
                  {contact.address && (
                    <p className="text-slate-650 dark:text-slate-350 mt-1 leading-normal">{contact.address}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Histórico de Demandas Relacionadas */}
          <div className="space-y-3 pt-4 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Histórico de Demandas ({demands.length})</h3>
              <button 
                onClick={onViewDemands}
                className="text-[10px] text-blue-500 hover:underline font-bold"
              >
                Ver tudo na lista
              </button>
            </div>

            {loading ? (
              <div className="text-center py-4 text-xs text-slate-500 animate-pulse">Carregando demandas...</div>
            ) : demands.length === 0 ? (
              <p className="text-xs text-slate-500 italic py-2">Nenhuma demanda vinculada a este contato.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {demands.map((demand) => (
                  <div 
                    key={demand.id}
                    className="p-3 bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-slate-100 dark:border-slate-800/80 rounded-2xl flex items-center justify-between gap-3 text-xs transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-bold text-[10px] text-slate-400">{demand.protocol}</span>
                        <span className="text-[10px] font-semibold text-slate-400">•</span>
                        <span className="text-[10px] font-medium text-slate-450">{demand.themeArea}</span>
                      </div>
                      <p className="font-bold text-slate-800 dark:text-slate-200 truncate leading-snug">{demand.title}</p>
                    </div>
                    
                    <div className="flex items-center gap-3 shrink-0">
                      {demand.valueRequested && (
                        <div className="text-right">
                          <p className="text-[9px] text-slate-400">Valor Solicitado</p>
                          <p className="font-bold text-slate-800 dark:text-slate-200">{formatCurrency(demand.valueRequested)}</p>
                        </div>
                      )}
                      <span className={`px-2 py-0.5 rounded font-bold text-[9px] ${getStatusColor(demand.status)}`}>
                        {demand.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-slate-150 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-750 text-slate-700 dark:text-slate-200 font-bold rounded-2xl text-xs transition-all"
          >
            Fechar
          </button>
        </div>

      </div>
    </div>
  );
};
